/**
 * Everything the club has sent one member, with each channel's fate — on the
 * member's own profile, so "did he get the reminder?" is a glance, not a hunt.
 */
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { memberCommsApi } from '../api/comms';
import { StatusBadge } from './DeliveryBadges';

export function MemberComms({ personId }: { personId: string }) {
  const { data, isLoading } = useQuery({
    queryKey: ['members', personId, 'comms'],
    queryFn: () => memberCommsApi.history(personId),
  });

  return (
    <section className="card">
      <div className="mb-3 flex items-center justify-between">
        <h2 className="text-sm uppercase tracking-[0.15em] text-gold-400">Communications</h2>
        <Link to="/comms?tab=history" className="text-xs text-cream-300/60 hover:text-gold-200">
          All club messages →
        </Link>
      </div>
      {isLoading ? (
        <p className="text-sm text-cream-300/60">Loading…</p>
      ) : !data?.length ? (
        <p className="text-sm text-cream-300/50">Nothing sent yet.</p>
      ) : (
        <ul className="divide-y divide-ink-800/60 text-sm">
          {data.map((m) => {
            const email = m.deliveries.find((d) => d.channel === 'email');
            const push = m.deliveries.find((d) => d.channel === 'push');
            return (
              <li key={m.notificationId} className="flex flex-wrap items-start justify-between gap-2 py-2">
                <div className="min-w-0">
                  <Link to={`/comms/${m.messageId}`} className="block truncate text-cream-100 hover:text-gold-200">
                    {m.title}
                  </Link>
                  <p className="text-[11px] text-cream-300/50">
                    {new Date(m.createdAt).toLocaleString()}
                    {m.eventTitle ? ` · ${m.eventTitle}` : ''}
                    {' · '}
                    {m.readAt ? 'read in app' : 'unread in app'}
                  </p>
                </div>
                <div className="flex flex-wrap items-center gap-1.5">
                  {email ? (
                    <span className="inline-flex items-center gap-1 text-[11px] text-cream-300/60">
                      email <StatusBadge status={email.status} />
                      {email.openedAt ? <span className="text-success">opened</span> : null}
                    </span>
                  ) : null}
                  {push ? (
                    <span className="inline-flex items-center gap-1 text-[11px] text-cream-300/60">
                      push <StatusBadge status={push.status} />
                    </span>
                  ) : null}
                  {!email && !push ? <span className="text-[11px] text-cream-300/40">inbox only</span> : null}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
